import * as React from "react";
import { DevButton, DevPanel, devPanelColors as c } from "@base/lib/dev-toolbar/DevPanel";
import { useDevTool } from "@base/lib/dev-toolbar/devTools";
import { getAllTrackerStatuses } from "../activeTrackers";
import {
  clearTrackingDebug,
  DebugEntry,
  getTrackingDebugEntries,
  subscribeTrackingDebug,
} from "./debugLog";

/**
 * The dev toolbar's "tracking" tool: which trackers are on (and why not), which
 * env var feeds each, and a live list of what every tracker was sent. Values
 * shown are all public anyway (they ship in the page source).
 */
export function TrackingDebugPanel() {
  const on = useDevTool("tracking");
  const entries = React.useSyncExternalStore(
    subscribeTrackingDebug,
    getTrackingDebugEntries,
    getTrackingDebugEntries,
  );

  if (!on) {
    return null;
  }

  const statuses = getAllTrackerStatuses();

  return (
    <DevPanel
      toolID="tracking"
      title="Tracking"
      side="right"
      actions={<DevButton onClick={clearTrackingDebug}>clear</DevButton>}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {statuses.map((s) => (
            <tr key={s.name} style={{ verticalAlign: "top" }}>
              <td style={{ ...cell, whiteSpace: "nowrap", color: s.on ? c.on : c.muted }}>
                {s.on ? "●" : "○"} {s.name}
              </td>
              <td style={{ ...cell, color: s.on ? c.text : c.warn }}>{s.reason}</td>
              <td style={cell}>
                {s.env.map((v) => (
                  <div key={v.name} style={{ color: v.value ? c.text : c.faint }}>
                    {v.name}
                    {v.value ? ` = ${truncate(v.value, 28)}` : " (not set)"}
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 8, paddingTop: 6, borderTop: `1px solid ${c.border}`, color: c.muted }}>
        Sent (newest first)
      </div>
      {entries.length === 0 && <div style={{ color: c.faint }}>Nothing yet.</div>}
      {groupByEvent(entries)
        .reverse()
        .map((group) => (
          <div key={group[0].id}>
            {group.map((entry) => (
              <EntryRow key={entry.id} entry={entry} />
            ))}
          </div>
        ))}
    </DevPanel>
  );
}

const cell: React.CSSProperties = { padding: "2px 6px 2px 0", wordBreak: "break-word" };

/**
 * Each of our events followed by what the trackers were sent for it, so a group
 * reads top-down while groups run newest first. Tracker lines before any event
 * (startup, e.g. GTM's init_ga4) get their own group.
 */
function groupByEvent(entries: DebugEntry[]): DebugEntry[][] {
  const groups: DebugEntry[][] = [];
  for (const entry of entries) {
    if (entry.tracker === null || groups.length === 0) {
      groups.push([entry]);
    } else {
      groups[groups.length - 1].push(entry);
    }
  }
  return groups;
}

function EntryRow({ entry }: { entry: DebugEntry }) {
  const detail = entry.detail === undefined ? "" : ` ${truncate(safeJson(entry.detail), 140)}`;
  if (entry.tracker === null) {
    return (
      <div style={{ marginTop: 6, color: "#f9fafb" }}>
        <span style={{ color: c.faint }}>{new Date(entry.time).toLocaleTimeString()} </span>
        <strong>{entry.label}</strong>
        <span style={{ color: c.muted }}>{detail}</span>
      </div>
    );
  }
  return (
    <div style={{ paddingLeft: 12, color: c.text, wordBreak: "break-all" }}>
      → <span style={{ color: c.accent }}>{entry.tracker}</span> {entry.label}
      <span style={{ color: c.muted }}>{detail}</span>
    </div>
  );
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
